import { useCallback, useState } from 'react';

interface UseCommentDialogProps<TStatus = any, TSubmitData = any> {
    onSubmit: (data: TSubmitData) => Promise<void> | void;
    requiresCommentStatuses?: TStatus[];
    createSubmitData: (status: TStatus, comment: string) => TSubmitData;
}

interface DialogState<TStatus = any> {
    open: boolean;
    status: TStatus | null;
    comment: string;
}

export function useCommentDialog<TStatus = any, TSubmitData = any>({
    onSubmit,
    requiresCommentStatuses = [],
    createSubmitData,
}: UseCommentDialogProps<TStatus, TSubmitData>) {
    const [dialogState, setDialogState] = useState<DialogState<TStatus>>({
        open: false,
        status: null,
        comment: '',
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const isCommentRequired =
        dialogState.status !== null &&
        requiresCommentStatuses.includes(dialogState.status);

    const canSubmit =
        !isSubmitting &&
        (!isCommentRequired ||
            (isCommentRequired && dialogState.comment.trim() !== ''));

    const openDialog = useCallback((status: TStatus) => {
        setDialogState({ open: true, status, comment: '' });
    }, []);

    const closeDialog = useCallback(() => {
        setDialogState({ open: false, status: null, comment: '' });
        setIsSubmitting(false);
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!dialogState.status || !canSubmit) return;

        try {
            setIsSubmitting(true);
            const submitData = createSubmitData(
                dialogState.status,
                dialogState.comment,
            );
            await onSubmit(submitData);
            closeDialog();
        } catch (error) {
            console.error('Submit failed:', error);
            setIsSubmitting(false);
            // You could add error handling here
        }
    }, [
        dialogState.status,
        dialogState.comment,
        canSubmit,
        createSubmitData,
        onSubmit,
        closeDialog,
    ]);

    const handleCommentChange = useCallback((comment: string) => {
        setDialogState((prev) => ({
            ...prev,
            comment,
        }));
    }, []);

    return {
        dialogState,
        isCommentRequired,
        canSubmit,
        isSubmitting,
        openDialog,
        closeDialog,
        handleSubmit,
        handleCommentChange,
    };
}
